"use client";

import React from "react";
import { Loader, Send, CircleCheck, CircleAlert } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
function ContactFormInner() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center py-12 text-center space-y-4 animate-in fade-in zoom-in duration-500">
        <div className="size-16 bg-primary/10 rounded-md flex items-center justify-center mb-4">
          <CircleCheck className="size-8 text-primary" />
        </div>
        <h3 className="text-2xl font-default font-bold text-foreground">
          Richiesta Inviata
        </h3>
        <p className="text-muted-foreground max-w-md">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="mt-6 rounded-md border-primary text-primary hover:bg-primary hover:text-white transition-colors">
          Invia una nuova richiesta
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField control={control} name="name" rules={{
        required: "Il nome è obbligatorio",
        minLength: {
          value: 2,
          message: "Minimo 2 caratteri"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-default font-medium uppercase tracking-wider text-xs">Nome Completo</FormLabel>
              <FormControl>
                <Input placeholder="Inserisci il tuo nome" className="rounded-md border-0 border-b border-input focus-visible:ring-0 focus-visible:border-primary bg-transparent" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "L'email è obbligatoria",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Indirizzo email non valido"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-default font-medium uppercase tracking-wider text-xs">Email </FormLabel>
              <FormControl>
                <Input type="email" placeholder="esempio@broker.it" className="rounded-md border-0 border-b border-input focus-visible:ring-0 focus-visible:border-primary bg-transparent" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField control={control} name="category" rules={{
        required: "Seleziona una categoria"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-default font-medium uppercase tracking-wider text-xs">Categoria Yacht</FormLabel>
              <FormControl>
                <Input placeholder="Entry, Premium o Gold" className="rounded-md border-0 border-b border-input focus-visible:ring-0 focus-visible:border-primary bg-transparent" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="guests" render={({
        field
      }) => <FormItem>
              <FormLabel className="font-default font-medium uppercase tracking-wider text-xs">Numero Ospiti</FormLabel>
              <FormControl>
                <Input type="number" placeholder="Es. 8" className="rounded-md border-0 border-b border-input focus-visible:ring-0 focus-visible:border-primary bg-transparent" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="message" rules={{
      required: "Il messaggio è obbligatorio",
      minLength: {
        value: 3,
        message: "Minimo 3 caratteri"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="font-default font-medium uppercase tracking-wider text-xs">Dettagli della Richiesta</FormLabel>
            <FormControl>
              <Textarea placeholder="Descrivi le tue preferenze, date desiderate e itinerari..." className="min-h-[120px] resize-y rounded-md border-0 border-b border-input focus-visible:ring-0 focus-visible:border-primary bg-transparent" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-md flex items-start gap-3">
          <CircleAlert className="size-5 text-destructive shrink-0 mt-0.5" />
          <p className="text-sm text-destructive">{submitError}</p>
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full sm:w-auto min-w-[240px] bg-primary text-white hover:bg-primary/90 rounded-md font-default font-bold uppercase tracking-widest transition-all border-b-2 border-secondary">
        {isSubmitting ? <>
            <Loader className="animate-spin mr-2 size-4" />
            Elaborazione...
          </> : <>
            <Send className="mr-2 size-4" />
            Richiedi Preventivo
          </>}
      </Button>
    </Form>;
}
export default function ContactUs() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="256678" id="inizia-il-tuo-viaggio" className="w-full py-16 md:py-24 bg-background text-foreground">
      <div className="container mx-auto px-4 md:px-6 max-w-5xl">
        <div ref={ref} className={`transition-all duration-700 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <div className="text-center mb-12 space-y-4">
            <h2 className="text-4xl md:text-5xl font-default font-bold text-foreground tracking-tighter uppercase">
              Inizia il Tuo Viaggio
            </h2>
            <div className="w-24 h-0.5 bg-secondary mx-auto mb-6"></div>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto font-default leading-relaxed">
              Consulenza esperta e risposte rapide. Riceverai una proposta personalizzata per il tuo charter a Ibiza entro 24 ore.
            </p>
          </div>

          <Card className="border-border shadow-xl bg-card rounded-md overflow-hidden">
            <CardHeader className="pb-6 border-b border-border/50 bg-muted/30">
              <CardTitle className="text-2xl font-default font-bold uppercase tracking-tight">Configura la tua Esperienza</CardTitle>
              <CardDescription className="font-default">
                Seleziona la categoria del vessel e specifica le tue necessità per un preventivo su misura.
              </CardDescription>
            </CardHeader>
            <CardContent className="pt-8">
              <FormProvider formKey="journey_form_contacts" formId="journey_form_contacts" sectionName="Contact Us Section" defaultValues={{
              name: "",
              email: "",
              category: "",
              guests: "",
              message: ""
            }} submitText="Invia Richiesta" successMessage="La tua richiesta è stata presa in carico. Un nostro broker ti contatterà con un preventivo dettagliato entro 24 ore." errorMessage="Si è verificato un errore durante l'invio. Ti preghiamo di riprovare o contattarci telefonicamente.">
                <ContactFormInner />
              </FormProvider>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>;
}